/**
 * The station registry administered from the Configuration page.
 *
 * A browser identifies its workstation by the name saved at registration under
 * {@link STATION_NAME_STORAGE_KEY}; Delphi uses the Windows computer name. Both look the
 * name up in `cashpoints`, whose id every financial record references, so renaming a
 * station moves its whole history to the computer that now carries the name. That is how
 * a replacement computer takes over a defective one, and how a station registered under
 * the wrong name is aligned with the one the old application created.
 *
 * Switching this computer to another station leaves the registry untouched: only the name
 * saved in this browser changes, and the app starts again as if it had always been that
 * station.
 */

/** A registered station: a cash drawer, or a workstation that handles no cash. */
export interface CashPointStation {
    id: number;
    name: string;
    isCashPoint: boolean;
}

/** The localStorage key holding this browser's station name, read at startup by `App`. */
export const STATION_NAME_STORAGE_KEY = 'stationName';

/** `cashpoints.Name` is `varchar(100)`. */
export const STATION_NAME_MAX_LENGTH = 100;

/** The i18n key under `configuration.stations` describing why a name cannot be saved. */
export type StationNameProblem = 'nameRequired' | 'nameTooLong' | 'nameTaken';

/** Normalises a typed name the way registration and the backend do. */
export const normalizeStationName = (typed: string): string => typed.trim().toUpperCase();

/**
 * Why `typed` cannot become the name of the station `stationId`, or `null` when it can.
 * Pass `null` for a station that is being created. Re-saving a station under its own name
 * is allowed; taking another station's name is not, because the startup lookup would then
 * match either of the two.
 */
export const stationNameProblem = (
    typed: string,
    stationId: number | null,
    stations: readonly CashPointStation[],
): StationNameProblem | null => {
    const name = normalizeStationName(typed);

    if (name === '') {
        return 'nameRequired';
    }
    if (name.length > STATION_NAME_MAX_LENGTH) {
        return 'nameTooLong';
    }
    if (stations.some(station => station.id !== stationId && station.name === name)) {
        return 'nameTaken';
    }
    return null;
};

/**
 * The i18n key under `configuration.stations` for a failed rename. The backend answers 409
 * with the error `CASH_POINT_NAME_TAKEN` when another station took the name after the list
 * was loaded; `useRequest` surfaces that error text as the thrown message.
 */
export const renameFailureKey = (error: unknown): 'nameTaken' | 'renameFailed' =>
    isNameTakenError(error) ? 'nameTaken' : 'renameFailed';

/** The i18n key under `configuration.stations` for a failed creation; same 409 as a rename. */
export const createFailureKey = (error: unknown): 'nameTaken' | 'createFailed' =>
    isNameTakenError(error) ? 'nameTaken' : 'createFailed';

function isNameTakenError(error: unknown): boolean {
    return error instanceof Error && error.message === 'CASH_POINT_NAME_TAKEN';
}

/** The i18n key under `configuration.stations` for a refused or failed change of a station's type. */
export type StationTypeFailureKey = 'sessionOpen' | 'safeStation' | 'typeChangeFailed';

const STATION_TYPE_REFUSALS: ReadonlyMap<string, StationTypeFailureKey> = new Map([
    ['CASH_POINT_SESSION_OPEN', 'sessionOpen'],
    ['CASH_POINT_IS_SAFE', 'safeStation'],
]);

/**
 * The i18n key for a failed `PUT /cash-drawer/stations/:id/cash-point`. The backend refuses (409)
 * to make a station with an open cash session a workstation - hand it over first - and to make
 * the safe one.
 */
export const stationTypeFailureKey = (error: unknown): StationTypeFailureKey =>
    (error instanceof Error ? STATION_TYPE_REFUSALS.get(error.message) : undefined) ??
    'typeChangeFailed';

/**
 * Whether `station` is the one this browser registered as. Switching this computer to
 * another station is offered for every other row.
 */
export const isThisComputersStation = (
    station: CashPointStation,
    storedStationName: string | null,
): boolean => storedStationName !== null && station.name === storedStationName;

/**
 * The station name this browser should keep after a rename. Only the browser that was
 * using the old name follows it; any other keeps its own, or it would silently switch to
 * a different station at its next start.
 */
export const storedStationNameAfterRename = (
    storedStationName: string | null,
    previousName: string,
    newName: string,
): string | null => (storedStationName === previousName ? newName : storedStationName);
